import React, { useState } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';
import { useAccountData, useStats } from './AccountDataHooks';
import { Icons } from './AccountIcons';
import AccountDashboard from './AccountDashboard';
import UserProfile from './UserProfile';
import Subscription from './Subscription';
import './Account.css';
import { themes } from './constants'; // Import only themes
import { normalizeTheme } from './utils'; // Import normalizeTheme from utils

ChartJS.register(ArcElement, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const Account = ({ user }) => {
  const [activeAccountTab, setActiveAccountTab] = useState('dashboard');

  // Fetch all necessary data using useAccountData
  const { userData, learnData, activitySummary, loading, formatDate } = useAccountData(user);

  // Initialize default learnScore
  const defaultLearnScore = themes.reduce((acc, theme) => ({
    ...acc,
    [normalizeTheme(theme)]: 0,
  }), {});

  // Merge userData.learnScore with defaultLearnScore
  const safeUserData = {
    ...userData,
    learnScore: { ...defaultLearnScore, ...(userData?.learnScore || {}) },
  };

  // Debug log to inspect userData and safeUserData
  console.log('Account.js - userData:', userData);
  console.log('Account.js - safeUserData:', safeUserData);

  // Unconditional hook calls
  const { getStatsForTab, calculateStreaks, getWeeklyProgressData, getCategoryPerformance, getBoxProgressionByCategory, getBadgeData: useBadgeData } = useStats(activitySummary, safeUserData, formatDate);
  const badgeData = useBadgeData();

  // Conditional logic after hooks
  if (!user) return <p className="error-message">Vous devez vous connecter pour accéder à cette page.</p>;
  if (loading) {
    return (
      <div className="loading">
        <span className="loading-title">
          <span>D</span>
          <span className="rotating">W</span>
          <span>A</span>
          <span>G</span>
        </span>
      </div>
    );
  }
  if (!safeUserData || !activitySummary) return <div className="error-message">Erreur lors du chargement des données.</div>;

  const userId = user.uid;
  console.log('Account.js - userId transmis à AccountDashboard:', userId);

  const statsToday = getStatsForTab("Aujourd'hui");
  const statsMonth = getStatsForTab('Mois');
  const statsTotal = {
    quizCount: safeUserData?.stats?.totalQuizzes || 0,
    flashcardsStudied: safeUserData?.stats?.flashcardsStudied || 0,
    performance: safeUserData?.performance || { byTheme: {} },
    sessionHistory: safeUserData?.sessionHistory || [],
    flashcardByCategory: safeUserData?.totalFlashcardsByTheme || {},
  };

  const streaksFromHook = calculateStreaks();
  const streaks = {
    currentStreak: streaksFromHook.current,
    maxStreak: streaksFromHook.longest,
  };

  const weeklyProgress = getWeeklyProgressData();
  const categoryPerformance = getCategoryPerformance();

  const chartColors = [
    'rgba(54, 162, 235, 0.8)',
    'rgba(255, 99, 132, 0.8)',
    'rgba(75, 192, 192, 0.8)',
    'rgba(255, 206, 86, 0.8)',
    'rgba(153, 102, 255, 0.8)',
    'rgba(255, 159, 64, 0.8)',
    'rgba(201, 203, 207, 0.8)',
    'rgba(123, 207, 123, 0.8)',
  ];
  const todayDisplay = formatDate(new Date()).split('-').reverse().join('/');
  const monthDisplay = new Date().toLocaleString('fr-FR', { month: 'long', year: 'numeric' });

  return (
    <div className="account-page">
      <div className="account-sidebar">
        <div className="sidebar-menu">
          <button className={`sidebar-item ${activeAccountTab === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveAccountTab('dashboard')}>
            <span className="sidebar-icon"><Icons.Activity /></span><span className="sidebar-text">Tableau de Bord</span>
          </button>
          <button className={`sidebar-item ${activeAccountTab === 'profile' ? 'active' : ''}`} onClick={() => setActiveAccountTab('profile')}>
            <span className="sidebar-icon"><Icons.Star /></span><span className="sidebar-text">Profil</span>
          </button>
          <button className={`sidebar-item ${activeAccountTab === 'subscription' ? 'active' : ''}`} onClick={() => setActiveAccountTab('subscription')}>
            <span className="sidebar-icon"><Icons.Zap /></span><span className="sidebar-text">Abonnement</span>
          </button>
        </div>
      </div>
      <div className="account-content">
        {activeAccountTab === 'dashboard' && (
          <AccountDashboard
            userId={userId}
            statsToday={statsToday}
            statsMonth={statsMonth}
            statsTotal={statsTotal}
            streaks={streaks}
            weeklyProgress={weeklyProgress}
            categoryPerformance={categoryPerformance}
            badgeData={badgeData}
            todayDisplay={todayDisplay}
            monthDisplay={monthDisplay}
            chartColors={chartColors}
            activitySummary={activitySummary}
            getBoxProgressionByCategory={getBoxProgressionByCategory}
            learnData={learnData}
            learnScore={safeUserData?.learnScore || defaultLearnScore}
          />
        )}
        {activeAccountTab === 'profile' && <UserProfile user={user} userData={safeUserData} />}
        {activeAccountTab === 'subscription' && <Subscription user={user} subscription={safeUserData?.subscription} />}
      </div>
    </div>
  );
};

export default Account;